import React, { useState, useEffect, useMemo } from "react";
import jsonMetaData from "../json/addon_form.json";
import { Form, Button, Modal, Spinner } from "react-bootstrap";
import axios from "axios";
import * as constants from "../api/constants";

const AdminMetaDataForm = ({
  type,
  data,
  selectedTab,
  togglePopup,
  categoryTypeDefault = "",
  onSaved,
}) => {
  const [formDataObject, setFormDataObject] = useState(() => ({ ...data }));
  const [uploadBusy, setUploadBusy] = useState(false);

  useEffect(() => {
    const fromApi = data?.categoryType ?? data?.category_type;
    setFormDataObject({
      ...(data || {}),
      categoryType:
        fromApi ||
        (type === "Add Addon" ? categoryTypeDefault : "") ||
        (type !== "Add Addon" ? type : "") ||
        categoryTypeDefault ||
        "",
    });
  }, [data, type, categoryTypeDefault]);

  const formElements = useMemo(() => {
    if (selectedTab === "Addons") {
      return type === "Add Addon"
        ? jsonMetaData.addNewAddon.formElements
        : jsonMetaData.addonsData.formElements;
    }
    const def = jsonMetaData[type];
    return def?.formElements || [];
  }, [selectedTab, type]);

  const changeOptions = (e, name, isCheckbox) => {
    setFormDataObject((prev) => ({
      ...prev,
      [name]: isCheckbox ? (e.target.checked ? "Y" : "N") : e.target.value,
    }));
  };

  const uploadAddonImage = async (file) => {
    if (!file?.type?.startsWith("image/")) {
      alert("Please choose an image file.");
      return;
    }
    setUploadBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await axios.post(constants.ADMIN_SITE_PAGE_MEDIA_UPLOAD, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const url = res.data?.url;
      if (typeof url === "string" && url.length) {
        setFormDataObject((prev) => ({ ...prev, imageName: url }));
      } else {
        alert("Upload response missing url — paste image path manually.");
      }
    } catch (e) {
      console.error(e);
      alert("Image upload failed. Is the API running?");
    } finally {
      setUploadBusy(false);
    }
  };

  const checkAllareFilled = (payloadObj) => {
    for (const [key, value] of Object.entries(payloadObj)) {
      if (key === "id") continue;
      if (value === null || value === "") return false;
    }
    return true;
  };

  const handleSave = () => {
    const payloadObj = {};
    formElements.forEach((element) => {
      if (element.type === "id") {
        if (formDataObject.id != null && formDataObject.id !== "")
          payloadObj.id = formDataObject.id;
        return;
      }
      if (element.show === false) return;
      payloadObj[element.name] = formDataObject[element.name] ?? "";
    });

    /* categoryType is show:false in addon_form.json but DB column CATEGORY_TYPE is NOT NULL */
    if (selectedTab === "Addons") {
      const cat =
        String(formDataObject.categoryType ?? "").trim() ||
        String(categoryTypeDefault ?? "").trim() ||
        (type !== "Add Addon" ? String(type).trim() : "");
      payloadObj.categoryType = cat;
      if (!payloadObj.allowMultiple || payloadObj.allowMultiple === "") {
        payloadObj.allowMultiple =
          formDataObject.allowMultiple != null && formDataObject.allowMultiple !== ""
            ? formDataObject.allowMultiple
            : "N";
      }
    }

    if (!checkAllareFilled(payloadObj)) {
      alert("Please fill all required fields");
      return;
    }

    if (type === "Add Addon") {
      if (!payloadObj.iconName) payloadObj.iconName = payloadObj.imageName || "default";
      if (payloadObj.allowMultiple == null || payloadObj.allowMultiple === "") {
        payloadObj.allowMultiple = "N";
      }
    }

    if (payloadObj.price !== undefined && payloadObj.price !== "") {
      payloadObj.price = Number(payloadObj.price);
    }

    axios
      .post(constants.ADMIN_ADD_UPDATE_ADDONS, payloadObj)
      .then(() => {
        alert("Saved successfully");
        togglePopup(false);
        if (typeof onSaved === "function") onSaved();
      })
      .catch((error) => {
        alert("Error saving. Please try again.");
        console.error(error);
      });
  };

  const renderFormElement = (element) => {
    if (!element.show) return null;

    if (element.name === "imageName" && selectedTab === "Addons") {
      return (
        <Form.Group key={element.name} className="mb-3" controlId={element.name}>
          <Form.Label>{element.label}</Form.Label>
          <Form.Control
            type="text"
            value={formDataObject[element.name] ?? ""}
            onChange={(e) => changeOptions(e, element.name)}
            placeholder="/media/site-page/… or bundled path"
          />
          <Form.Control
            type="file"
            accept="image/*"
            className="mt-2"
            disabled={uploadBusy}
            onChange={(e) => {
              const f = e.target.files?.[0];
              e.target.value = "";
              if (f) uploadAddonImage(f);
            }}
          />
          <Form.Text className="text-muted">
            {uploadBusy ? (
              <>
                <Spinner animation="border" size="sm" className="me-1" /> Uploading…
              </>
            ) : (
              "Uploads to the shared site-media folder (same as other admin uploads)."
            )}
          </Form.Text>
        </Form.Group>
      );
    }

    if (element.type === "text") {
      return (
        <Form.Group key={element.name} className="mb-3" controlId={element.name}>
          <Form.Label>{element.label}</Form.Label>
          <Form.Control
            type="text"
            value={formDataObject[element.name] ?? ""}
            onChange={(e) => changeOptions(e, element.name)}
            placeholder={element.placeholder}
          />
        </Form.Group>
      );
    }
    if (element.type === "number") {
      return (
        <Form.Group key={element.name} className="mb-3" controlId={element.name}>
          <Form.Label>{element.label}</Form.Label>
          <Form.Control
            type="number"
            value={formDataObject[element.name] ?? ""}
            onChange={(e) => changeOptions(e, element.name)}
            placeholder={element.placeholder}
          />
        </Form.Group>
      );
    }
    if (element.type === "boolean") {
      return (
        <div
          key={element.name}
          className="form-check form-check-inline mb-3"
          style={{ paddingTop: "0.5rem" }}
        >
          <input
            className="form-check-input"
            type="checkbox"
            id={element.name}
            checked={formDataObject[element.name] === "Y"}
            onChange={(e) => changeOptions(e, element.name, true)}
          />
          <label className="form-check-label" htmlFor={element.name}>
            {element.label}
          </label>
        </div>
      );
    }
    return null;
  };

  return (
    <>
      <Modal.Header closeButton>
        <Modal.Title>{type === "Add Addon" ? "Add add-on" : "Edit add-on"}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form>
          <div className="admin-meta-data-form">{formElements.map((el) => renderFormElement(el))}</div>
          <div style={{ color: "red" }} className="small">
            * Visible fields are mandatory
          </div>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={() => togglePopup(false)}>
          Cancel
        </Button>
        <Button variant="primary" onClick={() => handleSave()}>
          Save
        </Button>
      </Modal.Footer>
    </>
  );
};

export default AdminMetaDataForm;
